import React from 'react'

import {
  Button, Divider, Edit, Icon, InfoOutlined, Refresh, TagComponent as Tag,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, number, oneOf, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import { formatLongDate } from 'Utils/FormatDate'
import { NOOP } from 'Utils/stableDefaults'

import BillingInfoItem from './BillingInfoItem'
import CurrentPlanCard from './CurrentPlanCard'
import {
  PLAN_TYPE, SUBSCRIPTION_STATUS, TIER, resolveDisplayStatus,
} from '../../../constants'
import { RETRY_STATE } from '../../../hooks/useSubscription'
import { planChangesShape, scheduledChangeShape } from '../../../propTypes'

const PAYMENT_METHOD_PLACEHOLDER = '**** ****'

const STATUS_TAG_CONFIG = {
  [SUBSCRIPTION_STATUS.ACTIVE]: { color: 'primary', type: 'secondary' },
  [SUBSCRIPTION_STATUS.FULLY_CANCELED]: { color: 'grey', type: 'secondary' },
  [SUBSCRIPTION_STATUS.PENDING_CANCEL]: { color: 'warning', type: 'secondary' },
  [SUBSCRIPTION_STATUS.EXPIRED]: { color: 'grey', type: 'secondary' },
  [SUBSCRIPTION_STATUS.PAST_DUE]: { color: 'attention', type: 'secondary' },
  [SUBSCRIPTION_STATUS.PAUSED]: { color: 'grey', type: 'secondary' },
  [SUBSCRIPTION_STATUS.PAUSE_SCHEDULED]: { color: 'attention', type: 'secondary' },
  [SUBSCRIPTION_STATUS.REDEMPTION]: { color: 'attention', type: 'secondary' },
  [SUBSCRIPTION_STATUS.REJECTED]: { color: 'warning', type: 'secondary' },
  [SUBSCRIPTION_STATUS.SUSPENDED]: { color: 'warning', type: 'secondary' },
}

const DEFAULT_TAG = { color: 'grey', type: 'secondary' }


function PlanCard({
  statusLabel,
  status,
  tier = TIER.ESSENTIALS,
  canceledAt = null,
  canceledByAdmin = false,
  price,
  renewsOn = null,
  paymentMethod,
  subscriptionEndDate = null,
  periodEndDate = null,
  suspendedAt = null,
  accessEndsAt = null,
  isCancelLoading = false,
  onCancel: handleCancelSubscription = NOOP,
  onReactivate: handleReactivateSubscription = NOOP,
  onRenew: handleRenewSubscription = NOOP,
  isRestoreLoading = false,
  locale = 'en',
  updatePaymentUrl = '/profile/subscription/update-payment-method',
  onRetryPayment: handleRetryPayment = NOOP,
  retryState = RETRY_STATE.IDLE,
  planType = PLAN_TYPE.YEARLY,
  billingCycle = planType,
  onSwitchPlan: handleSwitchPlan = NOOP,
  subscriptionV16Enabled = false,
  scheduledPlanSwitchTo = null,
  scheduledPlanSwitchAt = null,
  onCancelPlanSwitch: handleCancelPlanSwitch = NOOP,
  isCancelPlanSwitchLoading = false,
  userPauseScheduledAt = null,
  userPauseScheduledUntil = null,
  userPause = false,
  pausedUntil = null,
  onResumePause: handleResumePause = NOOP,
  isResumeLoading = false,
  resumeRequested = false,
  resumeInProgress = false,
  plansRestructureEnabled = false,
  billingTerm = PLAN_TYPE.MONTHLY,
  termPrice = null,
  nextBillingDate = null,
  scheduledChange = null,
  planChanges = null,
}) {
  const displayStatus = resolveDisplayStatus(status, canceledAt, userPauseScheduledAt)
  const isFullyCanceled = displayStatus === SUBSCRIPTION_STATUS.FULLY_CANCELED
  const isPendingCancel = displayStatus === SUBSCRIPTION_STATUS.PENDING_CANCEL
  const isSuspended = displayStatus === SUBSCRIPTION_STATUS.SUSPENDED
  const isPaused = displayStatus === SUBSCRIPTION_STATUS.PAUSED
  const isPauseScheduled = displayStatus === SUBSCRIPTION_STATUS.PAUSE_SCHEDULED
  const isInactive = isFullyCanceled || displayStatus === SUBSCRIPTION_STATUS.EXPIRED || isSuspended
  // Flag off, cancelling is hidden while a pause is scheduled or live (Solidgate can't hold both).
  // Flag on, the BE replaces a scheduled pause and ends a live user pause right away.
  const canCancelDuringPause = plansRestructureEnabled && (isPauseScheduled || (isPaused && userPause))
  const canCancel = !isInactive && !isPendingCancel && ((!isPaused && !isPauseScheduled) || canCancelDuringPause)
  // The user can act on a pause they scheduled; operational (chargeback/admin) pauses
  // never set userPause, so no resume control is rendered for them.
  const showCancelScheduledPause = isPauseScheduled
  const showResumePause = isPaused && userPause && !resumeRequested
  // isResumeLoading clears before the success hit finishes flushing, so the request is still
  // pending past it; the control stays disabled until the reload rather than vanishing.
  const isResumePending = isResumeLoading || resumeRequested

  const tagConfig = STATUS_TAG_CONFIG[displayStatus] || DEFAULT_TAG
  const formattedEndDate = formatLongDate(subscriptionEndDate, locale)

  const translate = (key, options) => I18n.t(key, { ns: 'rails', lng: locale, ...options })

  let displayStatusLabel = statusLabel
  if (isFullyCanceled || isPendingCancel) {
    displayStatusLabel = translate('subscription_management.active.badge_cancelled')
  }
  if (isPaused) displayStatusLabel = translate('subscription_management.paused.badge_paused')
  if (isPauseScheduled) displayStatusLabel = translate('subscription_management.paused.badge_scheduled')

  const hasScheduledDowngrade = scheduledPlanSwitchTo === PLAN_TYPE.MONTHLY
  const hasAnyScheduledSwitch = Boolean(scheduledPlanSwitchTo)
  // When an upgrade to yearly is scheduled, show annual plan info immediately
  const displayPlanType = scheduledPlanSwitchTo === PLAN_TYPE.YEARLY ? PLAN_TYPE.YEARLY : planType
  // Plan-type fact, independent of the flag, so monthly subscribers keep their billing-cycle
  // copy when the flag is off. The plan-switch button stays flag-gated (it is a v1.6 feature).
  const isMonthlyPlan = displayPlanType === PLAN_TYPE.MONTHLY
  // CGTrader AI has a single cadence, so there is no plan to switch to. handleSwitchPlan derives
  // the opposite cadence unconditionally and would preview an ai_studio/yearly plan that the
  // backend does not define.
  const isAiPlan = tier === TIER.AI_STUDIO
  // CGTrader AI names the next charge in the banner too (per design). renewsOn is the serialized
  // period end and is null once a cancellation is scheduled, which falls back to the plain copy.
  const monthlyRenewalMessage = isAiPlan && renewsOn
    ? translate('subscription_management.active.monthly_renewal_message_ai', { date: renewsOn })
    : translate('subscription_management.active.monthly_renewal_message')
  const showRenewalInfo =
    !hasScheduledDowngrade &&
    [
      SUBSCRIPTION_STATUS.ACTIVE,
      SUBSCRIPTION_STATUS.PAUSE_SCHEDULED,
      SUBSCRIPTION_STATUS.PAUSED,
    ].includes(displayStatus) &&
    (isMonthlyPlan || formattedEndDate)
  const priceForBanner = price.replace(' / month', '/mo')

  let nextBillingValue = renewsOn || translate('subscription_management.active.billing.no_renewal')
  if (isSuspended) {
    nextBillingValue = translate('subscription_management.active.billing.billing_suspended')
  } else if (isPaused) {
    nextBillingValue = pausedUntil
      ? translate('subscription_management.active.billing.auto_resume', {
        date: formatLongDate(pausedUntil, locale),
      })
      : translate('subscription_management.paused.badge_paused')
  } else if (isPauseScheduled) {
    nextBillingValue = translate('subscription_management.active.billing.pauses_on', {
      date: formatLongDate(userPauseScheduledAt, locale),
    })
  }

  const hasActivePaymentMethod =
    Boolean(paymentMethod) && paymentMethod !== PAYMENT_METHOD_PLACEHOLDER
  const isRetryLoading = retryState === RETRY_STATE.LOADING
  const showRetryRow =
    displayStatus === SUBSCRIPTION_STATUS.REDEMPTION
    && hasActivePaymentMethod
    && retryState !== RETRY_STATE.SUBMITTED

  const retryRow = (
    <div className="plan-card__retry-row" aria-live="polite">
      <div className="plan-card__retry-action">
        <Button
          type="secondary-with-primary-border"
          size="small"
          customClass="plan-card__retry-button"
          icon={<Icon svgComponent={Refresh} size="tiny" color="primary" />}
          onClick={handleRetryPayment}
          disabled={isRetryLoading}
          aria-disabled={isRetryLoading}
        >
          {isRetryLoading
            ? translate('subscription_management.retry_payment.button_loading')
            : translate('subscription_management.retry_payment.button')}
        </Button>
        <p className="plan-card__retry-helper cgt-legacy-text-default cgt-mb-0">
          {translate('subscription_management.retry_payment.helper_text')}
        </p>
      </div>
    </div>
  )

  const updatePaymentLink = (
    <Button
      type="ghost"
      size="xsmall"
      icon={<Icon svgComponent={Edit} size="tiny" color="primary" />}
      customClass={plansRestructureEnabled
        ? 'billing-info-item__link cgt-body-base-semibold billing-info-item__link--restructure'
        : 'billing-info-item__link'}
      href={updatePaymentUrl}
    >
      {translate('subscription_management.active.billing.update')}
    </Button>
  )

  if (plansRestructureEnabled) {
    return (
      <CurrentPlanCard
        tier={tier}
        displayStatus={displayStatus}
        paymentMethod={paymentMethod}
        billingTerm={billingTerm}
        termPrice={termPrice}
        nextBillingDate={nextBillingDate}
        pausedUntil={pausedUntil}
        pauseScheduledAt={userPauseScheduledAt}
        pauseResumesAt={userPauseScheduledUntil}
        accessEndsAt={accessEndsAt}
        endedOn={status === SUBSCRIPTION_STATUS.PAUSED && canceledAt
          ? formatLongDate(canceledAt, locale, 'UTC')
          : periodEndDate}
        suspendedAt={suspendedAt}
        updatePaymentUrl={updatePaymentUrl}
        updatePaymentAction={!isInactive && updatePaymentLink}
        canCancel={canCancel && !canceledByAdmin}
        isCancelLoading={isCancelLoading}
        onCancel={handleCancelSubscription}
        canResume={showResumePause}
        isResumeLoading={isResumeLoading}
        onResume={handleResumePause}
        canCancelScheduledPause={showCancelScheduledPause && !canceledByAdmin}
        isCancelScheduledPauseLoading={isResumePending}
        resumeInProgress={resumeInProgress}
        canRetry={showRetryRow}
        isRetryLoading={isRetryLoading}
        isRetrySubmitted={displayStatus === SUBSCRIPTION_STATUS.REDEMPTION && retryState === RETRY_STATE.SUBMITTED}
        onRetry={handleRetryPayment}
        canceledByAdmin={canceledByAdmin}
        canRestore={!canceledByAdmin}
        isRestoreLoading={isRestoreLoading}
        onRenew={handleRenewSubscription}
        onReactivate={handleReactivateSubscription}
        scheduledChange={scheduledChange}
        canSwitchPlan={Boolean(planChanges) && !planChanges.blockedReason}
        onSwitchPlan={handleSwitchPlan}
        onKeepPlan={handleCancelPlanSwitch}
        isKeepPlanLoading={isCancelPlanSwitchLoading}
        locale={locale}
      />
    )
  }

  let planCardModifier = ''
  if (hasScheduledDowngrade) planCardModifier = ' plan-card--with-scheduled-switch'
  else if (showRenewalInfo) planCardModifier = ' plan-card--with-renewal'

  return (
    <div className={`plan-card${planCardModifier}`}>
      {hasScheduledDowngrade && (
        <div className="plan-card__scheduled-switch">
          <div className="plan-card__scheduled-switch-message">
            {/* SVG direct render to avoid Icon component color specificity conflict */}
            <InfoOutlined width="20" height="20" className="plan-card__scheduled-switch-icon" aria-hidden="true" />
            <p className="cgt-text-medium cgt-mb-0">
              <strong>{translate('subscription_management.scheduled_switch_banner.title')}</strong>
              {' - '}
              {translate('subscription_management.scheduled_switch_banner.description_prefix')}
              {' '}
              <strong>
                {translate(`subscription_management.switch_plan_modal.plan.${scheduledPlanSwitchTo}.name`)}
              </strong>
              {' '}
              {translate('subscription_management.scheduled_switch_banner.description_on')}
              {' '}
              <strong>{formatLongDate(scheduledPlanSwitchAt, locale)}</strong>
              {translate('subscription_management.scheduled_switch_banner.description_suffix', {
                price: priceForBanner,
              })}
            </p>
          </div>
          <Button
            type="secondary"
            size="small"
            customClass="plan-card__scheduled-switch-action plan-card__cancel-switch-button"
            onClick={handleCancelPlanSwitch}
            disabled={isCancelPlanSwitchLoading}
          >
            {translate('subscription_management.scheduled_switch_banner.cancel_button')}
          </Button>
        </div>
      )}
      {showRenewalInfo && (
        <div className="plan-card__renewal-info">
          <div className="plan-card__renewal-message">
            {/* SVG direct render to avoid Icon component color specificity conflict */}
            <InfoOutlined width="20" height="20" className="plan-card__renewal-icon" />
            <p className="cgt-text-medium cgt-mb-0">
              {isMonthlyPlan ? (
                <>
                  <strong>{translate('subscription_management.active.monthly_plan')}</strong>
                  {' - '}
                  {monthlyRenewalMessage}
                </>
              ) : (
                <>
                  <strong>{translate('subscription_management.active.annual_plan')}</strong>
                  {' - '}
                  {translate('subscription_management.active.renewal_message_prefix')}
                  <strong>{formattedEndDate}</strong>
                  {translate('subscription_management.active.renewal_message_suffix')}
                </>
              )}
            </p>
          </div>
          {subscriptionV16Enabled && !hasAnyScheduledSwitch && !isAiPlan
            && displayStatus === SUBSCRIPTION_STATUS.ACTIVE && (
            <Button
              type="ghost-primary"
              size="small"
              customClass="plan-card__renewal-action"
              onClick={handleSwitchPlan}
              data-testid="plan-card-switch-plan-button"
            >
              {translate('subscription_management.active.switch_plan')}
            </Button>
          )}
        </div>
      )}

      <div className="plan-card__header">
        <div className="plan-card__title-group">
          <h3 className="cgt-legacy-text-default plan-card__title">
            {translate(`subscription_management.active.plan_name.${tier}`)}
          </h3>
          <Tag
            color={tagConfig.color}
            type={tagConfig.type}
            small
          >
            {displayStatusLabel}
          </Tag>
        </div>
        {!canceledByAdmin && !isSuspended && (
          <>
            {isInactive && (
              <Button
                type="ghost-primary"
                size="small"
                onClick={handleReactivateSubscription}
                disabled={isRestoreLoading}
              >
                {translate('subscription_management.active.reactivate_button')}
              </Button>
            )}
            {isPendingCancel && (
              <Button
                type="ghost-primary"
                size="small"
                onClick={handleRenewSubscription}
                disabled={isRestoreLoading}
              >
                {translate('subscription_management.active.renew_button')}
              </Button>
            )}
            {showCancelScheduledPause && (
              <Button
                type="ghost-primary"
                size="small"
                onClick={handleResumePause}
                disabled={isResumePending}
              >
                {translate('subscription_management.paused.cancel_scheduled_pause_button')}
              </Button>
            )}
            {showResumePause && (
              <Button
                type="ghost-primary"
                size="small"
                onClick={handleResumePause}
                disabled={isResumeLoading}
              >
                {translate('subscription_management.paused.resume_button')}
              </Button>
            )}
            {canCancel && (
              <Button
                type="ghost"
                size="small"
                customClass="plan-card__cancel-button"
                onClick={handleCancelSubscription}
                disabled={isCancelLoading}
              >
                {translate('subscription_management.active.cancel_button')}
              </Button>
            )}
          </>
        )}
      </div>

      <Divider customClass="plan-card__divider" />

      <div className="plan-card__billing-grid">
        <BillingInfoItem
          iconType="billingCycle"
          label={translate('subscription_management.active.billing.billing_cycle')}
          value={translate(`subscription_management.billing_cycle.${billingCycle}`)}
        />
        <BillingInfoItem
          iconType="renewsOn"
          label={translate('subscription_management.active.billing.next_billing_date')}
          value={nextBillingValue}
        />
        <BillingInfoItem
          iconType="price"
          label={translate('subscription_management.active.billing.price')}
          value={price}
        />
        <BillingInfoItem
          iconType="payment"
          label={translate('subscription_management.active.billing.payment_method')}
          value={paymentMethod}
          action={(canCancel || isPaused || isPauseScheduled) && updatePaymentLink}
        />
      </div>

      {showRetryRow && (
        <>
          <Divider customClass="plan-card__divider" />
          {retryRow}
        </>
      )}
    </div>
  )
}

PlanCard.propTypes = {
  statusLabel: string.isRequired,
  status: string.isRequired,
  tier: oneOf([TIER.ESSENTIALS, TIER.PREMIUM, TIER.AI_STUDIO]),
  canceledAt: string,
  canceledByAdmin: bool,
  price: string.isRequired,
  renewsOn: string,
  paymentMethod: string.isRequired,
  subscriptionEndDate: string,
  periodEndDate: string,
  suspendedAt: string,
  accessEndsAt: string,
  onCancel: func,
  isCancelLoading: bool,
  onReactivate: func,
  onRenew: func,
  isRestoreLoading: bool,
  locale: string,
  updatePaymentUrl: string,
  onRetryPayment: func,
  retryState: string,
  planType: oneOf([PLAN_TYPE.MONTHLY, PLAN_TYPE.YEARLY]),
  billingCycle: oneOf([PLAN_TYPE.MONTHLY, PLAN_TYPE.YEARLY]),
  onSwitchPlan: func,
  subscriptionV16Enabled: bool,
  scheduledPlanSwitchTo: string,
  scheduledPlanSwitchAt: string,
  onCancelPlanSwitch: func,
  isCancelPlanSwitchLoading: bool,
  userPauseScheduledAt: string,
  userPauseScheduledUntil: string,
  userPause: bool,
  pausedUntil: string,
  onResumePause: func,
  isResumeLoading: bool,
  resumeRequested: bool,
  resumeInProgress: bool,
  plansRestructureEnabled: bool,
  billingTerm: oneOf([PLAN_TYPE.MONTHLY, PLAN_TYPE.ANNUAL]),
  termPrice: number,
  nextBillingDate: string,
  scheduledChange: scheduledChangeShape,
  planChanges: planChangesShape,
}

export default PlanCard
